import { useDeferredValue, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { CurrentSubscriptionCard } from '@/components/customer/billing/CurrentSubscriptionCard';
import { NoActivePlanCard } from '@/components/customer/billing/NoActivePlanCard';
import { TrialExpiredBanner } from '@/components/customer/billing/TrialExpiredBanner';
import { BillingActionRequiredBanner } from '@/components/customer/billing/BillingActionRequiredBanner';
import { PlanCard } from '@/components/customer/billing/PlanCard';
import { CancelSubscriptionDialog } from '@/components/customer/billing/CancelSubscriptionDialog';
import { PaymentActionRequiredDialog } from '@/components/customer/billing/PaymentActionRequiredDialog';
import { useCustomerPlanManagement } from '@/hooks/customer/useCustomerPlanManagement';
import { customerPlanManagementKeys } from '@/hooks/customer/useCustomerPlanManagement';
import {
  useCancelSubscription,
  useDowngradeSubscription,
  useResumeSubscription,
  useUpgradeSubscription,
} from '@/hooks/customer/useCustomerSubscriptionActions';
import { extractJsonApiErrorMessage } from '@/lib/jsonapi/errors';
import {
  clearScheduledTransition,
  isTransitionExpired,
  loadScheduledTransition,
  type ScheduledTransitionRecord,
} from '@/lib/customer/scheduled-transitions';
import { resolveBillingCycleLabel } from '@/lib/billing-cycle-label';
import type { PaymentActionRequiredMeta } from '@/types/customer-plan-management';

export default function CustomerPlanManagementPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [planSearch, setPlanSearch] = useState('');
  const [paymentActionMeta, setPaymentActionMeta] = useState<PaymentActionRequiredMeta | null>(null);

  const planManagementQuery = useCustomerPlanManagement();
  const upgradeMutation = useUpgradeSubscription('/customer/billing/checkout', {
    onPaymentActionRequired: setPaymentActionMeta,
  });
  const downgradeMutation = useDowngradeSubscription();
  const cancelMutation = useCancelSubscription();
  const resumeMutation = useResumeSubscription();

  const payload = planManagementQuery.data?.data;
  const currentSubscription = payload?.currentSubscription;
  const currentSubscriptionId = currentSubscription?.id ?? null;
  const requiresReactivationCheckout = payload?.flags.requiresReactivationCheckout ?? false;
  const normalizedStatusLabel = (currentSubscription?.statusLabel ?? '').trim().toLowerCase();
  const isCancelledSubscription =
    currentSubscription?.displayStatus === 'cancelled' ||
    normalizedStatusLabel === 'cancelled' ||
    normalizedStatusLabel === 'canceled';
  const isSelectingPlan =
    !currentSubscription ||
    isCancelledSubscription ||
    requiresReactivationCheckout;
  const plans = payload?.plans ?? [];
  const upgradeEligibility = payload?.transitionEligibility.upgrade;
  const upgradeOptions = upgradeEligibility?.options ?? [];
  const downgradeEligibility = payload?.transitionEligibility.downgrade;
  const downgradeOptions = downgradeEligibility?.options ?? [];
  const canResume = payload?.flags.canResume ?? false;
  const hasPendingChange = payload?.flags.hasPendingChange ?? false;

  const deferredSearch = useDeferredValue(planSearch);
  const normalizedSearch = deferredSearch.trim().toLowerCase();

  const billingCycleOptions = useMemo(() => {
    const byId = new Map<string, { id: string; label: string; sortWeight: number }>();

    plans.forEach((plan) => {
      plan.pricing.forEach((price) => {
        if (!price.cycleId) return;
        const rawLabel = (price.cycleName ?? price.interval ?? '').trim();
        if (!rawLabel) return;

        const normalized = rawLabel.toLowerCase();
        const sortWeight = normalized.includes('month') ? 0 : normalized.includes('year') ? 1 : 2;

        const label = resolveBillingCycleLabel({ cycleName: price.cycleName, interval: price.interval }, t);
        if (!label) return;

        const existing = byId.get(price.cycleId);
        if (!existing || existing.sortWeight > sortWeight) {
          byId.set(price.cycleId, { id: price.cycleId, label, sortWeight });
        }
      });
    });

    return Array.from(byId.values()).sort((a, b) => {
      if (a.sortWeight !== b.sortWeight) return a.sortWeight - b.sortWeight;
      return a.label.localeCompare(b.label);
    });
  }, [plans]);

  const [selectedBillingCycleId, setSelectedBillingCycleId] = useState<string | null>(
    () => billingCycleOptions[0]?.id ?? null,
  );

  useEffect(() => {
    if (!billingCycleOptions.length) {
      if (selectedBillingCycleId !== null) setSelectedBillingCycleId(null);
      return;
    }

    if (!selectedBillingCycleId || !billingCycleOptions.some((option) => option.id === selectedBillingCycleId)) {
      setSelectedBillingCycleId(billingCycleOptions[0]?.id ?? null);
    }
  }, [billingCycleOptions, selectedBillingCycleId]);

  const checkoutSuccess = searchParams.get('checkout') === 'success';

  const onDismissSuccess = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('checkout');
    setSearchParams(next, { replace: true });
  };

  const [scheduledDowngrade, setScheduledDowngrade] = useState<ScheduledTransitionRecord | null>(null);

  useEffect(() => {
    const storageKey = currentSubscriptionId ?? 'current';
    const record = loadScheduledTransition(storageKey) ?? loadScheduledTransition('current');
    if (!record) {
      setScheduledDowngrade(null);
      return;
    }

    if (isTransitionExpired(record)) {
      clearScheduledTransition('current');
      if (currentSubscriptionId) {
        clearScheduledTransition(currentSubscriptionId);
      }
      setScheduledDowngrade(null);
      return;
    }

    setScheduledDowngrade(record);
  }, [currentSubscriptionId, planManagementQuery.dataUpdatedAt]);

  const formatDate = (value: string) => {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return value;
    }
    return new Intl.DateTimeFormat(i18n.language ?? 'en', { dateStyle: 'medium' }).format(parsed);
  };

  if (planManagementQuery.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-36 w-full" />
        <div className="grid gap-4 lg:grid-cols-3">
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      </div>
    );
  }

  if (planManagementQuery.isError || !payload) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('customer.planManagement.errors.loadTitle')}</AlertTitle>
        <AlertDescription>
          {extractJsonApiErrorMessage(planManagementQuery.error, t('customer.planManagement.errors.loadDescription'))}
        </AlertDescription>
      </Alert>
    );
  }

  const subscription = currentSubscription;
  const canShowCancelAction = subscription
    ? !isCancelledSubscription && subscription.displayStatus !== 'pending_cancellation'
    : false;

  const planEntries = plans
    .map((plan) => {
      const selectedCyclePrice =
        plan.pricing.find((price) => selectedBillingCycleId && price.cycleId === selectedBillingCycleId) ??
        plan.pricing[0] ??
        null;
      const isCurrentSelection =
        Boolean(selectedCyclePrice?.isCurrentSelection) ||
        Boolean(
          currentSubscription &&
            plan.id === currentSubscription.planId &&
            selectedCyclePrice &&
            (
              (currentSubscription.priceId && selectedCyclePrice.priceId === currentSubscription.priceId) ||
              (currentSubscription.cycleId && selectedCyclePrice.cycleId === currentSubscription.cycleId)
            ),
        );
      const statusAllowsTransitions =
        subscription
          ? subscription.displayStatus === 'active' ||
            subscription.displayStatus === 'trial' ||
            subscription.displayStatus === 'trial_expired' ||
            subscription.displayStatus === 'pending_cancellation' ||
            subscription.displayStatus === 'cancelled'
          : false;

      const currentPlan =
        currentSubscription?.planId ? plans.find((candidate) => candidate.id === currentSubscription.planId) : null;
      const currentPrice =
        currentPlan?.pricing.find((price) => {
          if (currentSubscription?.priceId && price.priceId === currentSubscription.priceId) {
            return true;
          }
          if (currentSubscription?.cycleId && price.cycleId === currentSubscription.cycleId) {
            return true;
          }
          return false;
        }) ?? null;

      const isSamePlanCycleSwitch =
        Boolean(
          currentSubscription &&
            plan.id === currentSubscription.planId &&
            selectedCyclePrice &&
            (
              (currentSubscription.priceId && selectedCyclePrice.priceId !== currentSubscription.priceId) ||
              (currentSubscription.cycleId && selectedCyclePrice.cycleId !== currentSubscription.cycleId)
            ),
        ) && !isCurrentSelection;

      const cycleSwitchDirection = (() => {
        if (!isSamePlanCycleSwitch || !selectedCyclePrice) {
          return null;
        }
        if (typeof selectedCyclePrice.price === 'number' && typeof currentPrice?.price === 'number') {
          if (selectedCyclePrice.price > currentPrice.price) return 'upgrade';
          if (selectedCyclePrice.price < currentPrice.price) return 'downgrade';
        }
        return 'upgrade';
      })();

      const canUpgrade =
        !isCurrentSelection &&
        (upgradeOptions.includes(plan.id) || (isSamePlanCycleSwitch && cycleSwitchDirection === 'upgrade'));
      const canDowngrade =
        !isCurrentSelection &&
        (downgradeOptions.includes(plan.id) || (isSamePlanCycleSwitch && cycleSwitchDirection === 'downgrade'));
      const isUpgrade = canUpgrade && statusAllowsTransitions && !requiresReactivationCheckout;

      const hasDirectAction =
        !isSelectingPlan && statusAllowsTransitions && !requiresReactivationCheckout && (canUpgrade || canDowngrade);

      const sortCents = (() => {
        const candidates = plan.pricing.filter((price) => typeof price.price === 'number');
        if (!candidates.length) return null;
        if (selectedBillingCycleId) {
          const match = candidates.find((price) => price.cycleId === selectedBillingCycleId);
          if (match) return match.price;
        }
        return candidates[0]?.price ?? null;
      })();

      return {
        plan,
        isCurrentSelection,
        isUpgrade,
        canDowngrade,
        selectedCyclePrice,
        hasDirectAction: isSelectingPlan ? !isCurrentSelection : hasDirectAction,
        mode: isSelectingPlan ? ('select' as const) : ('manage' as const),
        sortCents,
        sortWeight: isCurrentSelection ? 0 : isUpgrade ? 1 : canDowngrade ? 2 : 3,
      };
    })
    .filter(({ plan }) => {
      if (!normalizedSearch) {
        return true;
      }
      return `${plan.name} ${plan.tier ?? ''}`.toLowerCase().includes(normalizedSearch);
    })
    .sort((a, b) => {
      // Sort by price first (ascending). Plans without numeric prices go last.
      if (typeof a.sortCents === 'number' && typeof b.sortCents === 'number' && a.sortCents !== b.sortCents) {
        return a.sortCents - b.sortCents;
      }
      if (typeof a.sortCents === 'number' && typeof b.sortCents !== 'number') return -1;
      if (typeof a.sortCents !== 'number' && typeof b.sortCents === 'number') return 1;

      // Preserve action grouping as a secondary heuristic.
      if (a.sortWeight !== b.sortWeight) {
        return a.sortWeight - b.sortWeight;
      }

      return a.plan.name.localeCompare(b.plan.name);
    });

  const actionableCount = isSelectingPlan ? planEntries.length : planEntries.filter((entry) => entry.hasDirectAction).length;
  const isDowngradeScheduled = scheduledDowngrade?.type === 'downgrade';
  const actionsDisabled = !isSelectingPlan && (hasPendingChange || isDowngradeScheduled);
  const actionsDisabledReason = actionsDisabled ? t('customer.planManagement.actions.pendingChangeDisabled') : undefined;
  const hasBillingIssues =
    subscription
      ? subscription.displayStatus === 'past_due' ||
        subscription.displayStatus === 'unpaid' ||
        subscription.displayStatus === 'incomplete' ||
        subscription.displayStatus === 'incomplete_expired'
      : false;

  return (
    <div className="space-y-6">
      <PaymentActionRequiredDialog
        meta={paymentActionMeta}
        open={Boolean(paymentActionMeta)}
        onOpenChange={(open) => {
          if (!open) {
            setPaymentActionMeta(null);
          }
        }}
        onConfirmed={async () => {
          await queryClient.invalidateQueries({ queryKey: customerPlanManagementKeys.all });
        }}
      />
      {checkoutSuccess ? (
        <Alert>
          <AlertTitle>{t('customer.planManagement.checkout.successTitle')}</AlertTitle>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>{t('customer.planManagement.checkout.successDescription')}</span>
            <Button type="button" variant="ghost" onClick={onDismissSuccess}>
              {t('customer.planManagement.actions.dismiss')}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}

      {currentSubscription ? (
        <CurrentSubscriptionCard
          subscription={currentSubscription}
          actions={
            canShowCancelAction ? (
              <CancelSubscriptionDialog
                onConfirm={() => cancelMutation.mutate()}
                activeUntil={currentSubscription.nextBillingAt}
                isLoading={cancelMutation.isPending}
                disabled={cancelMutation.isPending}
              />
            ) : null
          }
        />
      ) : (
        <NoActivePlanCard />
      )}

      {hasBillingIssues ? (
        <BillingActionRequiredBanner
          status={currentSubscription!.displayStatus}
          onCompletePayment={() => navigate('/customer/billing/checkout?action=complete_checkout')}
        />
      ) : null}

      {currentSubscription && scheduledDowngrade?.type === 'downgrade' ? (
        <Alert>
          <AlertTitle>{t('customer.planManagement.notices.pendingDowngradeTitle')}</AlertTitle>
          <AlertDescription>
            {scheduledDowngrade.targetPlanName ? (
              scheduledDowngrade.activeUntil ? (
                t('customer.planManagement.notices.pendingDowngradeWithPlanAndDate', {
                  plan: scheduledDowngrade.targetPlanName,
                  date: formatDate(scheduledDowngrade.activeUntil),
                })
              ) : (
                t('customer.planManagement.notices.pendingDowngradeWithPlan', {
                  plan: scheduledDowngrade.targetPlanName,
                })
              )
            ) : scheduledDowngrade.activeUntil ? (
              t('customer.planManagement.feedback.downgradeScheduledWithDate', {
                date: formatDate(scheduledDowngrade.activeUntil),
              })
            ) : (
              t('customer.planManagement.feedback.downgradeScheduled')
            )}
          </AlertDescription>
        </Alert>
      ) : null}

      {currentSubscription?.trialExpired ? (
        <TrialExpiredBanner
          canPayNow={currentSubscription!.canPayNow}
          onPayNow={() => navigate('/customer/billing/checkout')}
        />
      ) : null}

      {isCancelledSubscription && (canResume || requiresReactivationCheckout) ? (
        <Alert>
          <AlertTitle>{t('customer.planManagement.reactivation.title')}</AlertTitle>
          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {requiresReactivationCheckout
                ? t('customer.planManagement.reactivation.checkoutRequired')
                : t('customer.planManagement.reactivation.resumeAllowed')}
            </span>
            <div className="flex items-center gap-2">
              {canResume ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => resumeMutation.mutate()}
                  disabled={resumeMutation.isPending}
                >
                  {resumeMutation.isPending
                    ? t('customer.planManagement.actions.processing')
                    : t('customer.planManagement.actions.resume')}
                </Button>
              ) : null}
              {requiresReactivationCheckout ? (
                <Button
                  type="button"
                  onClick={() => navigate('/customer/billing/checkout?action=reactivate')}
                >
                  {t('customer.planManagement.actions.activateSubscription')}
                </Button>
              ) : null}
            </div>
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-xl font-semibold">{t('customer.planManagement.plans.title')}</h2>
          <p className="text-sm text-muted-foreground">{t('customer.planManagement.plans.description')}</p>
          <p className="text-xs text-muted-foreground">
            {t('customer.planManagement.plans.summary', {
              total: plans.length,
              actionable: actionableCount,
            })}
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
          {billingCycleOptions.length > 1 && selectedBillingCycleId ? (
            <Tabs value={selectedBillingCycleId} onValueChange={(value) => setSelectedBillingCycleId(value)}>
              <TabsList>
                {billingCycleOptions.map((option) => (
                  <TabsTrigger key={option.id} value={option.id}>
                    {option.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          ) : null}
          <Input
            value={planSearch}
            onChange={(event) => setPlanSearch(event.target.value)}
            placeholder={t('customer.planManagement.plans.searchPlaceholder')}
            className="sm:w-72"
            aria-label={t('customer.planManagement.plans.searchAria')}
          />
        </div>
      </div>

      {planEntries.length ? (
        <div className="grid gap-4 lg:grid-cols-3">
          {planEntries.map(({ plan, isCurrentSelection, isUpgrade, canDowngrade, hasDirectAction, selectedCyclePrice }) => (
            isSelectingPlan ? (
              <PlanCard
                key={plan.id}
                mode="select"
                plan={plan}
                preferredCycleId={selectedBillingCycleId}
                currentSelectionOverride={isCurrentSelection}
                isLoading={upgradeMutation.isPending || downgradeMutation.isPending}
                onSelect={({ planId, priceId }) => {
                  const params = new URLSearchParams();
                  params.set('plan_id', planId);
                  if (priceId) {
                    params.set('price_id', priceId);
                  }
                  navigate(`/customer/billing/checkout?${params.toString()}`);
                }}
              />
            ) : (
              <PlanCard
                key={plan.id}
                plan={plan}
                isUpgrade={isUpgrade}
                canDowngrade={canDowngrade}
                hasDirectAction={hasDirectAction}
                preferredCycleId={selectedBillingCycleId}
                currentSelectionOverride={isCurrentSelection}
                isLoading={upgradeMutation.isPending || downgradeMutation.isPending}
                actionsDisabled={actionsDisabled}
                actionsDisabledReason={actionsDisabledReason}
                onUpgrade={() =>
                  upgradeMutation.mutate({
                    planId: plan.id,
                    priceId: selectedCyclePrice?.priceId ?? selectedCyclePrice?.id ?? null,
                    cycleId: selectedCyclePrice?.cycleId ?? null,
                  })
                }
                onDowngrade={() =>
                  downgradeMutation.mutate({
                    planId: plan.id,
                    priceId: selectedCyclePrice?.priceId ?? selectedCyclePrice?.id ?? null,
                    cycleId: selectedCyclePrice?.cycleId ?? null,
                  })
                }
              />
            )
          ))}
        </div>
      ) : (
        <Alert>
          <AlertTitle>{t('customer.planManagement.plans.emptyTitle')}</AlertTitle>
          <AlertDescription>{t('customer.planManagement.plans.emptyDescription')}</AlertDescription>
        </Alert>
      )}

      {/* Intentionally hide downgrade "no options available" messaging (per product request). */}
    </div>
  );
}
